import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../config/queryClient';

/**
 * Custom hook for optimistic updates when tasks are closed or completed
 * Handles React Query cache updates for remove/move operations
 */
export const useTaskActionsMutation = (taskStages) => {
  const queryClient = useQueryClient();

  /**
   * Remove a task from React Query cache (for deleted or archived tasks)
   */
  const removeTaskMutation = useMutation({
    mutationFn: async ({ taskId, columnId, filters = {} }) => {
      // This is an optimistic-only mutation, no API call needed
      // The actual API call was already done by the task delete/archive action
      return { taskId, columnId };
    },

    onMutate: async ({ taskId, columnId, filters = {} }) => {
      // Cancel outgoing queries to prevent race conditions
      await queryClient.cancelQueries([...queryKeys.tasks.column(columnId), filters]);

      // Store previous data for rollback
      const previousData = queryClient.getQueryData([...queryKeys.tasks.column(columnId), filters]);

      // Optimistically remove the task from the cache
      queryClient.setQueryData([...queryKeys.tasks.column(columnId), filters], (old) => {
        if (!old?.pages) return old;

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter((item) => item.task_id !== taskId),
            totalCount: Math.max(0, (page.totalCount || 0) - 1),
          })),
        };
      });

      return { previousData, columnId, filters };
    },

    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousData && context?.columnId && context?.filters) {
        queryClient.setQueryData([...queryKeys.tasks.column(context.columnId), context.filters], context.previousData);
      }
    },

    onSettled: (data, error, variables, context) => {
      // Invalidate to ensure consistency
      if (context?.columnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.tasks.column(context.columnId), context.filters],
        });
      }
    },
  });

  /**
   * Move a task to a different stage (for completed tasks)
   */
  const moveTaskToStageMutation = useMutation({
    mutationFn: async ({ taskId, sourceColumnId, targetColumnId, filters = {} }) => {
      // This is an optimistic-only mutation, no API call needed
      // The actual API call was already done by task update action
      return { taskId, sourceColumnId, targetColumnId };
    },

    onMutate: async ({ taskId, sourceColumnId, targetColumnId, filters = {} }) => {
      // Cancel outgoing queries for both columns
      await queryClient.cancelQueries([...queryKeys.tasks.column(sourceColumnId), filters]);
      await queryClient.cancelQueries([...queryKeys.tasks.column(targetColumnId), filters]);

      // Store previous data for rollback
      const previousSourceData = queryClient.getQueryData([...queryKeys.tasks.column(sourceColumnId), filters]);
      const previousTargetData = queryClient.getQueryData([...queryKeys.tasks.column(targetColumnId), filters]);

      // Get the task item from source column
      let taskItem = null;
      const sourceData = queryClient.getQueryData([...queryKeys.tasks.column(sourceColumnId), filters]);
      if (sourceData?.pages) {
        for (const page of sourceData.pages) {
          const found = page.items.find((item) => item.task_id === taskId);
          if (found) {
            taskItem = { ...found, task_stage: targetColumnId, task_status: 'completed' };
            break;
          }
        }
      }

      // Remove from source column
      queryClient.setQueryData([...queryKeys.tasks.column(sourceColumnId), filters], (old) => {
        if (!old?.pages) return old;

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter((item) => item.task_id !== taskId),
            totalCount: Math.max(0, (page.totalCount || 0) - 1),
          })),
        };
      });

      // Add to target column (if we found the task item)
      if (taskItem) {
        queryClient.setQueryData([...queryKeys.tasks.column(targetColumnId), filters], (old) => {
          if (!old?.pages) {
            // Initialize if no data exists
            return {
              pages: [
                {
                  items: [taskItem],
                  totalCount: 1,
                  hasMore: false,
                  page: 1,
                },
              ],
              pageParams: [1],
            };
          }

          // Add to first page
          return {
            ...old,
            pages: old.pages.map((page, index) => {
              if (index === 0) {
                return {
                  ...page,
                  items: [taskItem, ...page.items],
                  totalCount: (page.totalCount || 0) + 1,
                };
              }
              return page;
            }),
          };
        });
      }

      return { previousSourceData, previousTargetData, sourceColumnId, targetColumnId, filters };
    },

    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousSourceData && context?.sourceColumnId && context?.filters) {
        queryClient.setQueryData([...queryKeys.tasks.column(context.sourceColumnId), context.filters], context.previousSourceData);
      }
      if (context?.previousTargetData && context?.targetColumnId && context?.filters) {
        queryClient.setQueryData([...queryKeys.tasks.column(context.targetColumnId), context.filters], context.previousTargetData);
      }
    },

    onSettled: (data, error, variables, context) => {
      // Invalidate both columns to ensure consistency
      if (context?.sourceColumnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.tasks.column(context.sourceColumnId), context.filters],
        });
      }
      if (context?.targetColumnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.tasks.column(context.targetColumnId), context.filters],
        });
      }
    },
  });

  /**
   * Helper function to find the done stage
   */
  const findDoneStage = () => {
    return taskStages?.find((stage) => stage.task_stage_name?.toLowerCase() === 'done');
  };

  return {
    removeTaskMutation,
    moveTaskToStageMutation,
    findDoneStage,
  };
};
